import { TabletTaskNavBar } from '@euler/app/flows/task/components/navbar/TabletTaskNavBar';
import { TaskNavBar } from '@euler/app/flows/task/components/navbar/TaskNavBar';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useIsAsideLayout } from '@euler/components/Aside';
import { useIsInModalNavigation } from '@euler/components/ModalScrollView';
import { kAppStoreSnapshotMode } from '@euler/functions/constants';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FC, memo, ReactNode, RefObject, useCallback, useRef } from 'react';
import {
  Platform,
  RefreshControl as RNRefreshControl,
  ScrollViewProps,
  StyleProp,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import { createNativeWrapper, ScrollView } from 'react-native-gesture-handler';
import { runOnJS, SharedValue, useSharedValue } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTaskHeaderMetrics } from '../../../functions/useTaskHeaderMetrics';
import {
  TaskDetailHeaderShade,
  TaskDetailPageHeader,
} from './TaskDetailPageHeader';

const RefreshControl =
  Platform.OS === 'android'
    ? createNativeWrapper(RNRefreshControl, {
        disallowInterruption: true,
        shouldCancelWhenOutside: false,
      })
    : RNRefreshControl;

export type TaskDetailPageContainerProps = {
  isRefreshing?: boolean;
  licensePlateNo: string;
  vehicleName: string;
  vehicleImageUrl?: string | null;
  title?: string;
  subTitle?: string;
  showVehicleInfoForIpadNavHeader?: boolean;
  titleStyleForIpadNavHeader?: StyleProp<TextStyle>;
  contentContainerStyle?: StyleProp<ViewStyle>;
  offsetTop?: number;
  children?: ReactNode;
  footer?: ReactNode;
  scrollY?: SharedValue<number>;
  titleLoadingIndicator?: boolean;
  noDetailHeader?: boolean;
  scrollViewForTablet?: boolean;
  headerColors?: string[];
  navBarRightOptions?: () => ReactNode;
  renderContent?: (props: { paddingTop: number }) => ReactNode;
  onShowActions?: () => void;
  onRefresh?: () => void;
  scrollViewRef?: RefObject<ScrollView>;
  onScroll?: ScrollViewProps['onScroll'];
};

export const WiredTaskDetailPageContainer: FC<
  Omit<
    TaskDetailPageContainerProps,
    'licensePlateNo' | 'vehicleName' | 'vehicleImageUrl'
  >
> = memo(props => {
  const {
    detail: {
      basicInfo: { licensePlateNo, vehicleName, vehicleImageUrl, isMainTask },
    },
  } = useTaskContext();
  return (
    <TaskDetailPageContainer
      licensePlateNo={licensePlateNo}
      vehicleName={vehicleName}
      vehicleImageUrl={vehicleImageUrl}
      headerColors={isMainTask ? undefined : ['#009FD4', '#10c9bd']}
      isMainTask={isMainTask}
      {...props}
    />
  );
});

export const TaskDetailPageContainer: FC<
  TaskDetailPageContainerProps & {
    isMainTask?: boolean;
  }
> = memo(
  ({
    isMainTask,
    vehicleName,
    vehicleImageUrl,
    title,
    footer,
    showVehicleInfoForIpadNavHeader,
    titleStyleForIpadNavHeader,
    scrollViewForTablet,
    isRefreshing,
    contentContainerStyle,
    titleLoadingIndicator,
    noDetailHeader,
    onRefresh,
    children,
    renderContent,
    navBarRightOptions,
    headerColors,
    onShowActions,
    scrollViewRef,
    onScroll,
    ...props
  }) => {
    const { rpx } = useResponsiveContext();
    const isAside = useIsAsideLayout();
    const isModal = useIsInModalNavigation();
    const refreshRef = useRef(null);
    const fallbackScrollY = useSharedValue(0);
    const insets = useSafeAreaInsets();
    const scrollY = props.scrollY ?? fallbackScrollY;
    const { headerHeight } = useTaskHeaderMetrics();

    const { navBarHeight, isMobileLayout, safeAreaInsets, offset } =
      useTaskHeaderMetrics();

    const offsetTop = props.offsetTop ?? (noDetailHeader ? 0 : offset);

    const handleScroll: Exclude<ScrollViewProps['onScroll'], undefined> =
      useCallback(
        e => {
          'worklet';
          scrollY.value = e.nativeEvent.contentOffset.y;
          if (onScroll != null) {
            runOnJS(onScroll)(e);
          }
        },
        [onScroll, scrollY],
      );

    const licensePlateNo = kAppStoreSnapshotMode
      ? '沪AAAAAA'
      : props.licensePlateNo;

    if (!isAside && !isMobileLayout && !isModal) {
      return (
        <View
          css={`
            flex: 1;
          `}
        >
          <TabletTaskNavBar
            title={title ?? (isMainTask ? '任务详情' : '子任务详情')}
            licensePlateNo={licensePlateNo}
            vehicleName={vehicleName}
            titleLoadingIndicator={titleLoadingIndicator}
            rightOptions={navBarRightOptions}
            showVehicleInfo={showVehicleInfoForIpadNavHeader}
            titleStyle={titleStyleForIpadNavHeader}
            colors={headerColors}
            onShowActions={onShowActions}
          />
          <View
            css={`
              flex: 1;
              padding-bottom: ${safeAreaInsets.bottom}px;
            `}
          >
            {scrollViewForTablet ? (
              <ScrollView
                contentContainerStyle={[
                  {
                    paddingTop: rpx(15),
                    paddingLeft: rpx(15),
                    paddingRight: rpx(15),
                    paddingBottom: safeAreaInsets.bottom + rpx(80),
                    flexGrow: 1,
                  },
                ]}
              >
                {children}
              </ScrollView>
            ) : (
              <>{children}</>
            )}
          </View>
        </View>
      );
    }

    return (
      <View
        css={`
          flex: 1;
        `}
      >
        <TaskDetailHeaderShade
          scrollY={scrollY}
          offsetTop={offsetTop}
          colors={headerColors}
          headerHeight={headerHeight}
        />

        <TaskNavBar
          title={title ?? (isMainTask ? '任务详情' : '子任务详情')}
          licensePlateNo={licensePlateNo}
          vehicleName={vehicleName}
          scrollY={scrollY}
          titleLoadingIndicator={titleLoadingIndicator}
          rightOptions={navBarRightOptions}
          colors={headerColors}
          onShowActions={onShowActions}
        />

        {renderContent != null ? (
          renderContent({
            paddingTop:
              (noDetailHeader ? navBarHeight : headerHeight) - offsetTop,
          })
        ) : (
          <ScrollView
            ref={scrollViewRef}
            waitFor={Platform.OS === 'android' ? refreshRef : undefined}
            showsVerticalScrollIndicator={false}
            scrollEventThrottle={1}
            onScroll={handleScroll}
            refreshControl={
              <RefreshControl
                ref={refreshRef}
                refreshing={isRefreshing ?? false}
                onRefresh={onRefresh}
                progressViewOffset={navBarHeight}
              />
            }
            contentContainerStyle={[
              {
                paddingTop:
                  (noDetailHeader ? navBarHeight : headerHeight) - offsetTop,
                paddingLeft: rpx(15),
                paddingRight: rpx(15),
                paddingBottom: safeAreaInsets.bottom + rpx(44),
                flexGrow: 1,
              },
              contentContainerStyle,
            ]}
          >
            {children}
          </ScrollView>
        )}

        {!noDetailHeader && (
          <TaskDetailPageHeader
            licensePlateNo={licensePlateNo}
            vehicleName={vehicleName}
            vehicleImageUrl={vehicleImageUrl ?? undefined}
            scrollY={scrollY}
            offsetTop={offsetTop}
            headerHeight={headerHeight}
          />
        )}

        {footer ? (
          <View
            css={`
              box-shadow: 0 0 ${rpx(15)}px rgba(0, 0, 0, 0.25);
              background-color: #f8f8f8;
              padding-top: ${rpx(10)}px;
              padding-bottom: ${Math.max(insets.bottom, 10)}px;
              padding-left: ${rpx(15)}px;
              padding-right: ${rpx(15)}px;
            `}
          >
            {footer}
          </View>
        ) : null}
      </View>
    );
  },
);
